/**
 * Energy Role - Constants
 *
 * Fatores de conversão, símbolos e aliases para unidades de energia.
 *
 * Fontes:
 * - NIST SP 811 (2008)
 * - SI Brochure, 9th Edition (2019)
 * - CODATA 2018 (electronvolt)
 */

import type { SimpleUnitConfig, UnitAliases } from "../../contracts";

// =============================================================================
// TYPES
// =============================================================================

export type EnergyUnit =
  // SI
  | "gigajoule"
  | "megajoule"
  | "kilojoule"
  | "joule"
  | "millijoule"
  // Calories
  | "calorie"
  | "kilocalorie"
  // Watt-hour
  | "watt_hour"
  | "kilowatt_hour"
  | "megawatt_hour"
  | "gigawatt_hour"
  // BTU
  | "btu"
  | "therm"
  // Scientific
  | "electronvolt"
  | "kiloelectronvolt"
  | "megaelectronvolt"
  | "erg"
  // Mechanical
  | "foot_pound";

// =============================================================================
// UNITS CONFIG
// =============================================================================

/**
 * Configuração de cada unidade de energia.
 *
 * Base: joule (J) - unidade SI de energia
 * 1 joule = 1 newton × 1 metro = 1 kg⋅m²/s²
 *
 * Fatores exatos:
 * - SI: prefixos são potências de 10
 * - Calorie (IT): 4.1868 J (International Table, recomendado)
 * - Watt-hour: 3600 J (1 W × 1 h)
 * - BTU (IT): 1055.05585262 J (International Table)
 * - Electronvolt: 1.602176634e-19 J (SI 2019, exato)
 * - Erg: 1e-7 J (CGS)
 */
export const ENERGY_UNITS: Record<EnergyUnit, SimpleUnitConfig> = {
  // SI Units (todos exatos)
  gigajoule: {
    factor: 1_000_000_000,
    symbol: "GJ",
    singular: "gigajoule",
    plural: "gigajoules",
  },
  megajoule: {
    factor: 1_000_000,
    symbol: "MJ",
    singular: "megajoule",
    plural: "megajoules",
  },
  kilojoule: {
    factor: 1_000,
    symbol: "kJ",
    singular: "kilojoule",
    plural: "kilojoules",
  },
  joule: {
    factor: 1,
    symbol: "J",
    singular: "joule",
    plural: "joules",
  },
  millijoule: {
    factor: 0.001,
    symbol: "mJ",
    singular: "millijoule",
    plural: "millijoules",
  },

  // Calories (International Table - IT)
  // 1 cal (IT) = 4.1868 J (exato por definição)
  calorie: {
    factor: 4.1868,
    symbol: "cal",
    singular: "calorie",
    plural: "calories",
  },
  // 1 kcal = 1000 cal = 4186.8 J (= 1 "food Calorie")
  kilocalorie: {
    factor: 4186.8,
    symbol: "kcal",
    singular: "kilocalorie",
    plural: "kilocalories",
  },

  // Watt-hour (exatos)
  // 1 Wh = 1 W × 3600 s = 3600 J
  watt_hour: {
    factor: 3600,
    symbol: "Wh",
    singular: "watt-hour",
    plural: "watt-hours",
  },
  kilowatt_hour: {
    factor: 3_600_000,
    symbol: "kWh",
    singular: "kilowatt-hour",
    plural: "kilowatt-hours",
  },
  megawatt_hour: {
    factor: 3_600_000_000,
    symbol: "MWh",
    singular: "megawatt-hour",
    plural: "megawatt-hours",
  },
  gigawatt_hour: {
    factor: 3_600_000_000_000,
    symbol: "GWh",
    singular: "gigawatt-hour",
    plural: "gigawatt-hours",
  },

  // BTU (International Table)
  // 1 BTU (IT) = 1055.05585262 J (definição)
  btu: {
    factor: 1055.05585262,
    symbol: "BTU",
    singular: "BTU",
    plural: "BTUs",
  },
  // 1 therm = 100,000 BTU (IT)
  therm: {
    factor: 105505585.262,
    symbol: "thm",
    singular: "therm",
    plural: "therms",
  },

  // Scientific
  // 1 eV = 1.602176634e-19 J (SI 2019, exato por definição)
  electronvolt: {
    factor: 1.602176634e-19,
    symbol: "eV",
    singular: "electronvolt",
    plural: "electronvolts",
  },
  kiloelectronvolt: {
    factor: 1.602176634e-16,
    symbol: "keV",
    singular: "kiloelectronvolt",
    plural: "kiloelectronvolts",
  },
  megaelectronvolt: {
    factor: 1.602176634e-13,
    symbol: "MeV",
    singular: "megaelectronvolt",
    plural: "megaelectronvolts",
  },
  // 1 erg = 1e-7 J (CGS, exato)
  erg: {
    factor: 1e-7,
    symbol: "erg",
    singular: "erg",
    plural: "ergs",
  },

  // Mechanical
  // 1 ft⋅lbf = 1.3558179483314004 J (derivado de foot e pound-force)
  foot_pound: {
    factor: 1.3558179483314004,
    symbol: "ft⋅lbf",
    singular: "foot-pound",
    plural: "foot-pounds",
  },
};

// =============================================================================
// ALIASES
// =============================================================================

/**
 * Aliases para reconhecimento de unidades no cast
 */
export const ENERGY_ALIASES: UnitAliases = {
  // gigajoule
  gj: "gigajoule",
  // megajoule
  mj: "megajoule",
  // kilojoule
  kj: "kilojoule",
  // joule
  j: "joule",
  // calorie
  cal: "calorie",
  // kilocalorie
  kcal: "kilocalorie",
  Cal: "kilocalorie",
  // watt_hour
  wh: "watt_hour",
  // kilowatt_hour
  kwh: "kilowatt_hour",
  // megawatt_hour
  mwh: "megawatt_hour",
  // gigawatt_hour
  gwh: "gigawatt_hour",
  // btu
  btu: "btu",
  // therm
  thm: "therm",
  // electronvolt
  ev: "electronvolt",
  // kiloelectronvolt
  kev: "kiloelectronvolt",
  // megaelectronvolt
  mev: "megaelectronvolt",
  // erg
  erg: "erg",
  // foot_pound
  "ft-lb": "foot_pound",
  "ft-lbf": "foot_pound",
};

// =============================================================================
// HELPERS
// =============================================================================

/** Lista de todas as unidades */
export const ENERGY_UNIT_LIST = Object.keys(ENERGY_UNITS) as EnergyUnit[];

/** Fatores de conversão (para compatibilidade) */
export const ENERGY_FACTORS: Record<EnergyUnit, number> = Object.fromEntries(
  Object.entries(ENERGY_UNITS).map(([unit, config]) => [
    unit,
    config.factor ?? 1,
  ]),
) as Record<EnergyUnit, number>;
